import { RouteFactory } from "@app/routePaths";
import ImagePlaceholder from "@components/ArticleCard/components/ImagePlaceholder";
import type { ArticleCardProps } from "@components/ArticleCard/types";
import KeywordLink from "@components/ArticleMeta/KeywordLink";
import SubscriberFlag from "@components/ArticleMeta/SubscriberFlag/SubscriberFlag";
import TitleLink from "@components/ArticleMeta/TitleLink";
import UpdateTime from "@components/ArticleMeta/UpdateTime";
import { faCircle, faPlay } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { HeroVariant, TagsVariant, Type } from "@typings/enums";
import {
  IS_PLAYWRIGHT,
  VERTICAL_COLOUR_VARIANTS,
  VerticalName,
} from "@util/constant";
import { cn, getDefaultImage, getFormattedRedirectionUrl } from "@util/helpers";

import CreateTime from "../ArticleMeta/CreateTime";
import SectionLink from "../ArticleMeta/SectionLink";
import ArticleThumbnail from "../ArticleThumbnail/ArticleThumbnail";

export interface VerticalHeroProps extends ArticleCardProps {
  sectionLinkRootClassName?: string;
  verticalName: VerticalName;
  heroVariant: HeroVariant;
  showCreateTime?: boolean;
  tagsVariant: TagsVariant;
  sectionUniqueName: string;
  kicker?: string;
}

export default function VerticalHero({
  id = "",
  rootClassName = "",
  articleData,
  sectionLinkRootClassName = "",
  verticalName,
  heroVariant,
  showCreateTime = true,
  tagsVariant,
  sectionUniqueName,
  kicker = "",
}: VerticalHeroProps) {
  const {
    id: articleId,
    sections = [],
    title,
    edited,
    created,
    updated,
    media,
    displaySetting,
    tags = [],
    paywall,
    urlPath,
  } = articleData;

  const componentId = id || `article_${articleId}`;

  const _media = media && "length" in media ? media?.[0] : media;
  const thumbnail = _media?.content
    ? _media.content
    : {
        url: getDefaultImage(sectionUniqueName),
        alt: title,
      };

  const isVideo =
    _media &&
    (_media.content?.type === Type.ArticleBrightcoveVideo ||
      _media.content?.type === Type.ArticleYoutubeVideo);

  const articleLink = urlPath
    ? getFormattedRedirectionUrl(urlPath)
    : RouteFactory.article(id);

  let tagsWrapperClass = "";
  let tagRootClassName = "";

  switch (tagsVariant) {
    case TagsVariant.Inside:
      tagsWrapperClass =
        "lg:absolute lg:bottom-0 lg:right-0 lg:flex-col lg:p-2 lg:text-white lg:gap-y-2";
      tagRootClassName = "lg:border-white";
      break;
    case TagsVariant.Outside:
      tagsWrapperClass = "lg:left-0 lg:top-full lg:mt-2 lg:absolute flex-wrap";
      break;
  }

  let titleLinkClass = "";

  switch (heroVariant) {
    case HeroVariant.Main:
      titleLinkClass = "text-xl lg:text-8xl";
      break;
    case HeroVariant.Secondary:
      titleLinkClass = "lg:text-md xl:text-xl";
      break;
  }

  return (
    <div
      id={componentId}
      data-testid={componentId}
      className={cn(
        "vertical-hero-test relative flex w-full gap-x-4",
        rootClassName
      )}
    >
      <div
        className={cn(
          "lg:w-full",
          heroVariant === HeroVariant.Main ? "w-4/10" : "w-120px"
        )}
      >
        {thumbnail ? (
          <div className={cn("relative")}>
            {!IS_PLAYWRIGHT ? (
              <>
                <ArticleThumbnail
                  thumbnail={thumbnail}
                  orientation="landscape"
                  imgRootClassName="aspect-3x2 w-full"
                  articleLink={articleLink}
                />
                {isVideo ? (
                  <div>
                    <FontAwesomeIcon
                      icon={faCircle}
                      inverse
                      className="fa-stack-2x"
                    />
                    <FontAwesomeIcon icon={faPlay} className="fa-stack-1x" />
                  </div>
                ) : null}
              </>
            ) : (
              <ImagePlaceholder aspectRatio={"aspect-3x2"} />
            )}
          </div>
        ) : null}
      </div>
      <div
        className={cn(
          heroVariant === HeroVariant.Main ? "w-6/10" : "w-full-120px",
          "flex flex-col",
          "lg:absolute lg:bottom-0 lg:left-0 lg:right-0 lg:order-2 lg:w-full lg:bg-gradient-to-b lg:from-transparent lg:to-black lg:p-4"
        )}
      >
        {sections && sections.length > 0 ? (
          <SectionLink
            rootClassName={cn(
              "typo-eyebrow",
              VERTICAL_COLOUR_VARIANTS[verticalName].textStyle,
              VERTICAL_COLOUR_VARIANTS[verticalName].linkStyle,
              sectionLinkRootClassName
            )}
            sectionName={sections?.[0]?.name}
            sectionUniqueName={sections?.[0]?.uniqueName}
          />
        ) : null}

        {kicker ? (
          <div
            className="typo-eyebrow break-words lg:text-white"
            data-testid="vertical-hero-kicker-component"
          >
            {kicker}
          </div>
        ) : null}

        {paywall && paywall.contentAccess === "1" ? (
          <SubscriberFlag className="lg:text-white" />
        ) : null}

        <TitleLink
          rootClassName={cn(
            "w-full font-semibold my-1 font-poppins leading-130",
            "lg:text-white lg:w-9/12",
            titleLinkClass
          )}
          spanClassName={cn({
            "lg:line-clamp-3": heroVariant === HeroVariant.Secondary,
          })}
          title={displaySetting?.displayHeadline || title}
          articleId={articleId}
          slug={articleLink}
        />

        {created ? (
          <div className="mt-auto flex gap-x-2">
            {showCreateTime && <CreateTime created={created} />}
            {displaySetting?.hasUpdatedTimeDisplayed ? (
              <UpdateTime
                updated={updated}
                edited={edited}
                rootClassName="text-red"
              />
            ) : null}
          </div>
        ) : null}
        {tags ? (
          <div
            className={cn(
              "mt-2 flex gap-2 text-3xs text-gray-675",
              tagsWrapperClass
            )}
          >
            {tags.slice(0, 2).map((tag) => {
              return (
                <KeywordLink
                  key={tag.name}
                  tagName={tag.name}
                  tagUrlPath={tag.urlPath}
                  className={tagRootClassName}
                />
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
}
